import React, { useState, useCallback, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Paper,
  Link,
} from "@mui/material";
import { CloudUpload, Description, Close } from "@mui/icons-material";
import { Chip, Stack } from "@mui/material";
import { transitionGraphService, CreateGraphFromLogsResponse } from "../services/transitionGraphService";
import { TransitionGraphResultDialog } from "./TransitionGraphResultDialog";
import {
  OPERATION_LOG_SIZE_OVER_MESSAGE,
  OPERATION_LOG_SIZE_OVER_GUIDANCE_TEXT,
  OPERATION_LOG_SIZE_OVER_MANUAL_URL,
} from "../config/operationLogUpload";
import { is_operation_log_file_size_over_limit } from "../utils/operationLogFileSize";

type OperationLogSchema = {
  exportInfo: Record<string, unknown>;
  testCases: unknown[];
};

type CalibrationLogSchema = {
  session: Record<string, unknown> & {
    captures: unknown[];
  };
};

const is_record = (value: unknown): value is Record<string, unknown> => {
  return typeof value === "object" && value !== null && !Array.isArray(value);
};

const is_operation_log_schema = (value: unknown): value is OperationLogSchema => {
  if (!is_record(value)) {
    return false;
  }
  if (!is_record(value.exportInfo)) {
    return false;
  }
  return Array.isArray(value.testCases);
};

const is_calibration_log_schema = (value: unknown): value is CalibrationLogSchema => {
  if (!is_record(value)) {
    return false;
  }
  const session = value.session;
  if (!is_record(session)) {
    return false;
  }
  return Array.isArray(session.captures);
};

interface TransitionGraphCreateDialogProps {
  open: boolean;
  onClose: () => void;
  workspaceId: string;
  projectId: string;
}

export const TransitionGraphCreateDialog: React.FC<
  TransitionGraphCreateDialogProps
> = ({
  open,
  onClose,
  workspaceId,
  projectId,
}) => {
  const [selectedOperationLogFiles, setSelectedOperationLogFiles] = useState<File[]>([]);
  const [selectedGraphFile, setSelectedGraphFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [operationLogDragOver, setOperationLogDragOver] = useState(false);
  const [graphDragOver, setGraphDragOver] = useState(false);
  const [resultDialogOpen, setResultDialogOpen] = useState(false);
  const [graphResult, setGraphResult] = useState<CreateGraphFromLogsResponse | null>(null);

  // ダイアログが開かれたときに状態をリセット
  useEffect(() => {
    if (open) {
      setSelectedOperationLogFiles([]);
      setSelectedGraphFile(null);
      setLoading(false);
      setError(null);
      setOperationLogDragOver(false);
      setGraphDragOver(false);
    }
  }, [open]);

  // 操作ログファイル選択処理（複数ファイル対応）
  const handleOperationLogFileSelect = useCallback((files: File[]) => {
    const jsonFiles: File[] = [];
    const invalidFiles: string[] = [];

    files.forEach((file) => {
      if (file.type === "application/json" || file.name.endsWith(".json")) {
        jsonFiles.push(file);
      } else {
        invalidFiles.push(file.name);
      }
    });

    if (invalidFiles.length > 0) {
      setError(`以下のファイルはJSONファイルではありません: ${invalidFiles.join(", ")}`);
    } else {
      setError(null);
    }

    const validSizeFiles = jsonFiles.filter(
      (f) => !is_operation_log_file_size_over_limit(f)
    );

    if (jsonFiles.length > validSizeFiles.length) {
      setError(OPERATION_LOG_SIZE_OVER_MESSAGE);
    }

    if (validSizeFiles.length > 0) {
      setSelectedOperationLogFiles((prev) => {
        const existingNames = new Set(prev.map((f) => f.name));
        const newFiles = validSizeFiles.filter(
          (f) => !existingNames.has(f.name)
        );
        return [...prev, ...newFiles];
      });
    }
  }, []);

  // Graphファイル選択処理
  const handleGraphFileSelect = useCallback((file: File) => {
    if (file.type === "text/markdown" || file.name.endsWith(".md")) {
      setSelectedGraphFile(file);
      setError(null);
    } else {
      setError("Markdownファイルを選択してください");
    }
  }, []);

  // 操作ログファイルドロップ処理（複数ファイル対応）
  const handleOperationLogDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setOperationLogDragOver(false);

      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        handleOperationLogFileSelect(files);
      }
    },
    [handleOperationLogFileSelect]
  );

  const handleOperationLogDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setOperationLogDragOver(true);
  }, []);

  const handleOperationLogDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setOperationLogDragOver(false);
  }, []);

  // Graphファイルドロップ処理
  const handleGraphDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setGraphDragOver(false);

      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        handleGraphFileSelect(files[0]);
      }
    },
    [handleGraphFileSelect]
  );

  const handleGraphDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setGraphDragOver(true);
  }, []);

  const handleGraphDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setGraphDragOver(false);
  }, []);

  // 操作ログファイル入力変更処理（複数ファイル対応）
  const handleOperationLogFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        handleOperationLogFileSelect(Array.from(files));
      }
    },
    [handleOperationLogFileSelect]
  );

  // 操作ログファイル削除処理
  const handleRemoveOperationLogFile = useCallback((index: number) => {
    setSelectedOperationLogFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  // Graphファイル入力変更処理
  const handleGraphFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        handleGraphFileSelect(files[0]);
      }
    },
    [handleGraphFileSelect]
  );

  // Graph生成処理
  const handleGenerate = async () => {
    if (selectedOperationLogFiles.length === 0) {
      setError("操作ログファイルを選択してください");
      return;
    }

    if (!workspaceId || !projectId) {
      setError("ワークスペースIDとプロジェクトIDが必要です");
      return;
    }

    const hasSizeOver = selectedOperationLogFiles.some((file) =>
      is_operation_log_file_size_over_limit(file)
    );
    if (hasSizeOver) {
      setError(OPERATION_LOG_SIZE_OVER_MESSAGE);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // 1. 操作ログファイルを読み込む（複数ファイル対応）
      const operationLogs = await Promise.all(
        selectedOperationLogFiles.map(async (file) => {
          const text = await file.text();
          try {
            const content = JSON.parse(text) as unknown;
            return {
              filename: file.name,
              content,
              parseError: null,
            };
          } catch (parseError: unknown) {
            return {
              filename: file.name,
              content: null,
              parseError,
            };
          }
        })
      );

      const invalidJsonFiles = operationLogs
        .filter((log) => log.parseError !== null)
        .map((log) => log.filename);

      if (invalidJsonFiles.length > 0) {
        setError("操作ログかキャリブレーションログ以外のファイルがアップロードされています。生成できません。");
        return;
      }

      const invalidLogFiles = operationLogs
        .filter(
          (log) =>
            !is_operation_log_schema(log.content) &&
            !is_calibration_log_schema(log.content)
        )
        .map((log) => log.filename);

      if (invalidLogFiles.length > 0) {
        setError("操作ログかキャリブレーションログ以外のファイルがアップロードされています。生成できません。");
        return;
      }

      const validOperationLogs = operationLogs as Array<{
        filename: string;
        content: OperationLogSchema | CalibrationLogSchema;
        parseError: null;
      }>;

      // 2. 既存Graphファイルを読み込む（存在する場合）
      let existingGraph: { filename: string; content: string } | undefined;
      if (selectedGraphFile) {
        const text = await selectedGraphFile.text();
        existingGraph = {
          filename: selectedGraphFile.name,
          content: text,
        };
      }

      // 3. APIを呼び出してGraphを生成
      const response = await transitionGraphService.createGraphFromLogs({
        workspaceId,
        projectId,
        operationLogs: validOperationLogs,
        existingGraph,
      });

      // 4. 生成結果をログに出力（デバッグ用）
      console.log("Graph生成成功:", {
        graphText: response.graph_text,
        transitions: response.transitions,
        statistics: response.statistics,
        message: response.message,
      });

      // 5. 結果ダイアログを表示
      setGraphResult(response);
      setResultDialogOpen(true);
      
      // 入力ダイアログは閉じる
      onClose();
    } catch (error: unknown) {
      console.error("Graph生成エラー:", error);
      const message =
        error instanceof Error && error.message
          ? error.message
          : "不明なエラーが発生しました";
      setError(`Graph生成に失敗しました: ${message}`);
    } finally {
      setLoading(false);
    }
  };

  // ダイアログクローズ処理
  const handleClose = () => {
    setSelectedOperationLogFiles([]);
    setSelectedGraphFile(null);
    setError(null);
    setLoading(false);
    onClose();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { minHeight: "400px" },
        }}
      >
      <DialogTitle>
      遷移情報のGraphデータ生成
      </DialogTitle>

      <DialogContentText sx={{ px: 3, mb: 2 }}>
        操作ログから遷移情報のGraphデータを生成します。
      </DialogContentText>

      <DialogContent>
        <Box display="flex" flexDirection="column" gap={3}>
          {/* エラー表示（サイズオーバー時は回避策案内リンクも表示） */}
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error === OPERATION_LOG_SIZE_OVER_MESSAGE ? (
                <>
                  {OPERATION_LOG_SIZE_OVER_MESSAGE}{" "}
                  {(() => {
                    const parts = OPERATION_LOG_SIZE_OVER_GUIDANCE_TEXT.split(
                      "{{MANUAL_LINK}}"
                    );
                    return (
                      <>
                        {parts.map((part, i) => (
                          <React.Fragment key={i}>
                            {part}
                            {i < parts.length - 1 && (
                              <Link
                                href={OPERATION_LOG_SIZE_OVER_MANUAL_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ fontWeight: 600 }}
                              >
                                こちら
                              </Link>
                            )}
                          </React.Fragment>
                        ))}
                      </>
                    );
                  })()}
                </>
              ) : (
                error
              )}
            </Alert>
          )}

          {/* 操作ログファイルアップロードセクション */}
          <Box>
            <Typography variant="h6" gutterBottom>
              操作ログファイル
            </Typography>

            {/* ドロップゾーン */}
            <Paper
              sx={{
                border: "2px dashed",
                borderColor: operationLogDragOver ? "primary.main" : "grey.300",
                backgroundColor: operationLogDragOver ? "action.hover" : "background.paper",
                p: 4,
                textAlign: "center",
                cursor: "pointer",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  borderColor: "primary.main",
                  backgroundColor: "action.hover",
                },
              }}
              onDrop={handleOperationLogDrop}
              onDragOver={handleOperationLogDragOver}
              onDragLeave={handleOperationLogDragLeave}
              onClick={() => document.getElementById("operation-log-file-input")?.click()}
            >
              <input
                id="operation-log-file-input"
                type="file"
                accept=".json"
                multiple
                style={{ display: "none" }}
                onChange={handleOperationLogFileInputChange}
              />

              {selectedOperationLogFiles.length > 0 ? (
                <Box>
                  <Description
                    sx={{ fontSize: 48, color: "success.main", mb: 1 }}
                  />
                  <Typography variant="h6" color="success.main" gutterBottom>
                    {selectedOperationLogFiles.length}個のファイルが選択されています
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={1}
                    flexWrap="wrap"
                    useFlexGap
                    sx={{ mt: 2, justifyContent: "center" }}
                  >
                    {selectedOperationLogFiles.map((file, index) => (
                      <Chip
                        key={`${file.name}-${index}`}
                        label={`${file.name} (${(file.size / 1024).toFixed(1)} KB)`}
                        onDelete={() => handleRemoveOperationLogFile(index)}
                        deleteIcon={<Close />}
                        color="success"
                        variant="outlined"
                        sx={{ mb: 1 }}
                      />
                    ))}
                  </Stack>
                </Box>
              ) : (
                <Box>
                  <CloudUpload
                    sx={{ fontSize: 48, color: "text.secondary", mb: 1 }}
                  />
                  <Typography variant="h6" color="text.secondary">
                    JSONファイルをドロップまたはクリックして選択
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    操作ログのJSONファイルを選択してください（複数選択可）
                  </Typography>
                </Box>
              )}
            </Paper>
          </Box>

          {/* 既存の遷移情報のGraphファイルアップロードセクション */}
          <Box>
            <Typography variant="h6" gutterBottom>
              既存の遷移情報のGraph（オプション）
            </Typography>

            {/* ドロップゾーン */}
            <Paper
              sx={{
                border: "2px dashed",
                borderColor: graphDragOver ? "primary.main" : "grey.300",
                backgroundColor: graphDragOver ? "action.hover" : "background.paper",
                p: 4,
                textAlign: "center",
                cursor: "pointer",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  borderColor: "primary.main",
                  backgroundColor: "action.hover",
                },
              }}
              onDrop={handleGraphDrop}
              onDragOver={handleGraphDragOver}
              onDragLeave={handleGraphDragLeave}
              onClick={() => document.getElementById("graph-file-input")?.click()}
            >
              <input
                id="graph-file-input"
                type="file"
                accept=".md"
                style={{ display: "none" }}
                onChange={handleGraphFileInputChange}
              />

              {selectedGraphFile ? (
                <Box>
                  <Description
                    sx={{ fontSize: 48, color: "success.main", mb: 1 }}
                  />
                  <Typography variant="h6" color="success.main">
                    {selectedGraphFile.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    サイズ: {(selectedGraphFile.size / 1024).toFixed(1)} KB
                  </Typography>
                </Box>
              ) : (
                <Box>
                  <CloudUpload
                    sx={{ fontSize: 48, color: "text.secondary", mb: 1 }}
                  />
                  <Typography variant="h6" color="text.secondary">
                    Markdownファイルをドロップまたはクリックして選択
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    既存の遷移情報のGraph（.mdファイル）を選択してください（任意）
                  </Typography>
                </Box>
              )}
            </Paper>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          キャンセル
        </Button>
        <Button
          onClick={handleGenerate}
          variant="contained"
          disabled={
            selectedOperationLogFiles.length === 0 ||
            loading ||
            !workspaceId ||
            !projectId
          }
          startIcon={loading ? <CircularProgress size={20} /> : undefined}
        >
          {loading ? "生成中..." : "Graph生成"}
        </Button>
      </DialogActions>
    </Dialog>

    {/* 結果表示ダイアログ */}
    <TransitionGraphResultDialog
      open={resultDialogOpen}
      onClose={() => setResultDialogOpen(false)}
      result={graphResult}
    />
    </>
  );
};

